import { useTranslation } from 'react-i18next';
import { useSEO, generateWebPageSchema, generateBreadcrumbSchema } from '@/utils/seo';

/* ─── Values Hero Background ─── */
const VALUES_HERO_BG = 'https://readdy.ai/api/search-image?query=Elegant%20abstract%20golden%20compass%20rose%20and%20guiding%20light%20on%20deep%20navy%20blue%20background%20minimal%20sophisticated%20high-end%20corporate%20aesthetic%20soft%20glowing%20rays%20luxurious%20atmosphere%20no%20text&width=1920&height=800&seq=valueshero1&orientation=landscape';
import ElegantHeader from '@/components/feature/ElegantHeader';
import ElegantFooter from '@/components/feature/ElegantFooter';
import ElegantHero from '@/components/feature/ElegantHero';
import ElegantSectionTitle from '@/components/feature/ElegantSectionTitle';
import { useScrollReveal } from '@/hooks/useScrollReveal';

export default function ValuesPage() {
  const { t, i18n } = useTranslation();

  const getSchemaLang = () => {
    const map: Record<string, string> = { ja: 'ja-JP', en: 'en-US', ko: 'ko-KR', 'zh-TW': 'zh-TW' };
    return map[i18n.language] || 'ja-JP';
  };

  const { ref: valuesRef, visible: valuesVisible } = useScrollReveal({ threshold: 0.1 });

  useSEO({
    title: t('seo.valuesTitle'),
    description: t('seo.valuesDescription'),
    keywords: t('seo.valuesKeywords'),
    path: '/values',
    schema: {
      '@context': 'https://schema.org',
      '@graph': [
        generateWebPageSchema(t('seo.valuesTitle'), t('seo.valuesDescription'), '/values', getSchemaLang()),
        generateBreadcrumbSchema([
          { name: t('nav.topPage'), path: '/' },
          { name: t('nav.values'), path: '/values' },
        ]),
      ],
    },
  });

  const values = [
    { title: t('values.clientFirst'), description: t('values.clientFirstDesc') },
    { title: t('values.dataDriven'), description: t('values.dataDrivenDesc') },
    { title: t('values.innovation'), description: t('values.innovationDesc') },
    { title: t('values.integrity'), description: t('values.integrityDesc') },
    { title: t('values.collaboration'), description: t('values.collaborationDesc') },
    { title: t('values.improvement'), description: t('values.improvementDesc') },
  ];

  return (
    <div className="min-h-screen" style={{ background: '#FAF9F6' }}>
      <ElegantHeader />

      <ElegantHero
        title={t('values.title')}
        subtitle={t('values.subtitle')}
        tag="Values"
        backgroundImage={VALUES_HERO_BG}
        breadcrumb={[
          { label: t('nav.topPage'), to: '/' },
          { label: t('nav.values') },
        ]}
      />

      <main className="py-20 md:py-28 px-6 md:px-10 max-w-[1100px] mx-auto">
        <div className="mb-16">
          <ElegantSectionTitle
            tag="Our Values"
            title={t('values.sixValues')}
          />
        </div>

        <div ref={valuesRef as React.RefObject<HTMLDivElement>} className="space-y-0">
          {values.map((value, index) => (
            <section
              key={index}
              className="py-8 border-b transition-colors duration-300"
              style={{
                borderColor: 'rgba(212, 207, 196, 0.4)',
                opacity: valuesVisible ? 1 : 0,
                transform: valuesVisible ? 'translateY(0)' : 'translateY(20px)',
                transition: `opacity 0.7s ease ${index * 0.1}s, transform 0.7s ease ${index * 0.1}s, border-color 0.3s`,
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLElement).style.borderBottomColor = 'rgba(201, 162, 39, 0.3)';
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLElement).style.borderBottomColor = 'rgba(212, 207, 196, 0.4)';
              }}
            >
              <div className="flex items-start gap-6">
                <div
                  className="shrink-0 w-12 h-12 flex items-center justify-center"
                  style={{
                    border: '1px solid rgba(201, 162, 39, 0.25)',
                    background: 'rgba(201, 162, 39, 0.05)',
                  }}
                >
                  <span
                    className="text-lg"
                    style={{
                      fontFamily: "'Playfair Display', serif",
                      color: '#C9A227',
                    }}
                  >
                    {String(index + 1).padStart(2, '0')}
                  </span>
                </div>
                <div>
                  <h2
                    className="text-xl md:text-2xl mb-3"
                    style={{
                      fontFamily: "'Noto Serif JP', 'Playfair Display', serif",
                      fontWeight: 500,
                      color: '#1A1A1A',
                    }}
                  >
                    {value.title}
                  </h2>
                  <p
                    className="text-sm md:text-base leading-relaxed"
                    style={{
                      color: '#7A7569',
                      fontFamily: "'Noto Serif JP', serif",
                    }}
                  >
                    {value.description}
                  </p>
                </div>
              </div>
            </section>
          ))}
        </div>
      </main>

      <ElegantFooter />
    </div>
  );
}
